import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Anchor, Select, TextInput } from '@mantine/core';
import { Link } from 'react-router-dom';
import { useMediaQueryIndex } from '../../../helpers/hooks';
import AppFlexbox from '../../common/AppFlexbox';
import { ECOM_PRODUCT_STATUS_ENUM } from '../../../config/constants';
import AppStack from '../../common/AppStack';
import EditProductCard from '../products/EditProductCard';
import AppText from '../../common/AppText';
import VendorMediaSortableDropzone from '../media/VendorMediaSortableDropzone';
import AppTextEditor from '../../common/AppTextEditor';
import StoreBundlePricingEditControls from './StoreBundlePricingEditControls';
import StoreProductAssignFormSection from '../stores/StoreProductAssignFormSection';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import ProductImageShowcase from '../products/ProductImageShowcase';
import AppTextEditorContent from '../../common/AppTextEditorContent';
import StoreBundlePricingInfo from './StoreBundlePricingInfo';

const StoreFixedBundleEdit = ({
  ecomStoreProductPackage,
  isSubmitting,
  loading,
  canEditProducts,
  formState,
  setFormState,
  minPrice,
  maxPrice,
  productUrl
}) => {
  const { state: authState } = useContext(AuthContext);
  const { isLaptopOrSmaller } = useMediaQueryIndex();

  return (
    <AppFlexbox style={{ flexDirection: isLaptopOrSmaller ? 'column' : 'row' }}>
      <AppStack
        style={{
          flex: 2,
          gap: 16
        }}
      >
        <StoreProductAssignFormSection
          disabled={!canEditProducts}
          enableQuantityChange
          hideBundleProducts
          isSubmitting={isSubmitting}
          loading={loading}
          onChange={(products) => {
            setFormState({
              ...formState,
              ecomVendorBundleProducts: products
            });
          }}
          selectedProducts={formState.ecomVendorBundleProducts}
          showPrice
          subTitle={
            canEditProducts
              ? 'Select the product that will be sold together in this bundle.'
              : null
          }
          title="Included products"
        />

        <EditProductCard title="Pricing">
          <StoreBundlePricingEditControls
            disabled={isSubmitting}
            onChange={(pricing) =>
              setFormState({
                ...formState,
                ...pricing
              })
            }
            pricing={{
              pricingAmountOverride: formState.pricingAmountOverride,
              roundingAdjustmentOverride: formState.roundingAdjustmentOverride,
              fkEcomPricingType: formState.fkEcomPricingType
            }}
          />
        </EditProductCard>

        {canEditProducts || !ecomStoreProductPackage ? (
          <EditProductCard
            title="Bundle details"
            titleAction={
              productUrl ? (
                <Anchor
                  component={Link}
                  style={{ fontSize: 14, color: 'dodgerblue' }}
                  to={productUrl}
                >
                  View product
                </Anchor>
              ) : null
            }
          >
            <AppStack>
              <TextInput
                disabled={isSubmitting}
                label="Title"
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    name: e.currentTarget.value
                  })
                }
                required
                value={formState.name}
              />

              <AppStack style={{ gap: 5 }}>
                <AppTextEditor
                  characterLimit={1250}
                  disabled={isSubmitting}
                  label="Description"
                  onChange={(v) => {
                    setFormState({
                      ...formState,
                      description: v
                    });
                  }}
                  value={formState.description}
                />
              </AppStack>

              <AppStack style={{ gap: 5 }}>
                <VendorMediaSortableDropzone
                  canChangeImages
                  disabled={isSubmitting}
                  fkEcomStore={authState.pkEcomStore}
                  media={formState.ecomVendorProductMedia}
                  onChange={(media) => {
                    setFormState({
                      ...formState,
                      ecomVendorProductMedia: media
                    });
                  }}
                  title={
                    <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                      Media
                    </AppText>
                  }
                />
              </AppStack>
            </AppStack>
          </EditProductCard>
        ) : (
          <EditProductCard title="Preview">
            <AppStack style={{ gap: 8 }}>
              <ProductImageShowcase
                images={
                  ecomStoreProductPackage.ecomVendorBundle
                    .ecomVendorProductMedia.length > 0
                    ? ecomStoreProductPackage.ecomVendorBundle
                        .ecomVendorProductMedia
                    : ecomStoreProductPackage.ecomVendorBundle.ecomVendorBundleProducts
                        .map((p) =>
                          p.previewImage
                            ? {
                                pkEcomVendorProductMedia: p.uuid,
                                src: p.previewImage,
                                sort: p.sort
                              }
                            : null
                        )
                        .filter(Boolean)
                }
                maxHeight={450}
              />
              <AppStack style={{ gap: 0 }}>
                <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                  {formState.name}
                </AppText>

                <AppTextEditorContent content={formState.description} />
              </AppStack>
            </AppStack>
          </EditProductCard>
        )}
      </AppStack>

      <AppStack
        style={{ flex: 1, maxWidth: isLaptopOrSmaller ? 'unset' : 300 }}
      >
        <EditProductCard title="Status">
          <Select
            data={[
              { value: ECOM_PRODUCT_STATUS_ENUM.ACTIVE, label: 'Active' },
              { value: ECOM_PRODUCT_STATUS_ENUM.DRAFT, label: 'Draft' }
            ]}
            disabled={isSubmitting}
            onChange={(v) => {
              setFormState({
                ...formState,
                fkEcomProductStatus: v
              });
            }}
            value={formState.fkEcomProductStatus ?? ''}
          />
        </EditProductCard>

        <EditProductCard title="Pricing">
          {formState.ecomVendorBundleProducts.length === 0 ? (
            <AppStack style={{ gap: 5 }}>
              <AppText
                style={{
                  fontSize: 14,
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center'
                }}
              >
                No products selected yet, add products to preview bundle
                pricing.
              </AppText>
            </AppStack>
          ) : (
            <StoreBundlePricingInfo
              fkEcomPricingType={formState.fkEcomPricingType}
              maxVendorPrice={maxPrice}
              minVendorPrice={minPrice}
              pricingAmount={formState.pricingAmountOverride}
              roundingAdjustment={formState.roundingAdjustmentOverride}
            />
          )}
        </EditProductCard>
      </AppStack>
    </AppFlexbox>
  );
};

StoreFixedBundleEdit.propTypes = {
  formState: PropTypes.object.isRequired,
  setFormState: PropTypes.func.isRequired,
  canEditProducts: PropTypes.bool,
  ecomStoreProductPackage: PropTypes.object,
  isSubmitting: PropTypes.bool,
  loading: PropTypes.bool,
  maxPrice: PropTypes.number,
  minPrice: PropTypes.number,
  productUrl: PropTypes.string
};

export default StoreFixedBundleEdit;
